import React, { useCallback, useEffect, useState } from 'react';
import type { Page } from '../types';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { getFirestoreDB } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from '../lib/firebase';
import BackToPosButton from '../components/common/BackToPosButton';

interface FeedbackRow {
  id: string;
  userId?: string;
  category?: string;
  message?: string;
  status?: string;
  adminReply?: string | null;
  createdAt?: { toMillis?: () => number } | number;
}

interface CustomerAppFeedbackPageProps {
  onNavigate?: (page: Page) => void;
}

/**
 * Feedback inbox — prefers reading mirrored `app_feedback` on POS Firestore
 * (filled by customer-app Functions). Falls back to empty with instructions.
 */
const CustomerAppFeedbackPage: React.FC<CustomerAppFeedbackPageProps> = ({ onNavigate }) => {
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const db = getFirestoreDB();
      const q = query(collection(db, 'app_feedback'), orderBy('createdAt', 'desc'), limit(50));
      const snap = await getDocs(q);
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<FeedbackRow, 'id'>) })));
    } catch (err) {
      setRows([]);
      setError(
        err instanceof Error
          ? err.message
          : 'Could not load app_feedback. Deploy customer-app mirror or sync feedback into POS.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const reply = async (id: string) => {
    const text = (replyDraft[id] || '').trim();
    if (!text) return;
    try {
      const call = httpsCallable(getFirebaseFunctions(), 'replyCustomerAppFeedback');
      await call({ feedbackId: id, reply: text });
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Reply failed — deploy replyCustomerAppFeedback on POS Functions.');
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="bg-white shadow-md px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Customer app feedback</h1>
          <p className="text-sm text-gray-500">Replaces mobile admin-web feedback inbox.</p>
        </div>
        <div className="flex items-center gap-3">
          {onNavigate && (
            <button type="button" className="text-sm text-pink-600" onClick={() => onNavigate('loyalty')}>
              ← Loyalty &amp; offers
            </button>
          )}
          <BackToPosButton />
        </div>
      </header>
      <main className="flex-1 p-6 overflow-y-auto space-y-4">
        {error && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">{error}</p>}
        {rows.length === 0 && !error && (
          <p className="text-sm text-gray-500">No feedback mirrored yet.</p>
        )}
        {rows.map((row) => (
          <div key={row.id} className="bg-white border rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm text-gray-500">
              <span>{row.category || 'feedback'}</span>
              <span>{row.status || 'open'}</span>
            </div>
            <p className="text-gray-900 whitespace-pre-wrap">{row.message}</p>
            {row.adminReply && <p className="text-sm text-green-700">Reply: {row.adminReply}</p>}
            <div className="flex gap-2">
              <input
                className="flex-1 border rounded-md px-3 py-2 text-sm"
                placeholder="Reply…"
                value={replyDraft[row.id] || ''}
                onChange={(e) => setReplyDraft((p) => ({ ...p, [row.id]: e.target.value }))}
              />
              <button
                type="button"
                className="px-3 py-2 bg-pink-500 text-white rounded-md text-sm"
                onClick={() => void reply(row.id)}
              >
                Send
              </button>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
};

export default CustomerAppFeedbackPage;
